import { z } from "zod";
import type { Severity } from "./event-envelope.js";

/**
 * The notifications engine (#564): something happened in an environment ->
 * one activity row (`NotificationEvents`) -> fan-out to the people who may
 * see it and want it -> one outbox row per person and channel
 * (`NotificationOutbox`) -> the dispatcher sends it through the one sender.
 *
 * Tier-2 metadata only (ADR-0024): names, numbers and times the portal
 * already shows, never anything a caller said.
 */

/** What happened. A closed vocabulary: a type is added here, with its writer, its message and its category. */
export const NotificationTypeSchema = z.enum(["alarm.opened", "alarm.resolved", "reporting.stopped", "reporting.resumed"]);
export type NotificationType = z.infer<typeof NotificationTypeSchema>;

/** What a person chooses about. Every type maps to exactly one (`categoryOf`). */
export const NotificationCategorySchema = z.enum(["alarms-critical", "alarms-other", "stopped-reporting"]);
export type NotificationCategory = z.infer<typeof NotificationCategorySchema>;

export const NotificationChannelSchema = z.enum(["email"]);
export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

export function categoryOf(type: NotificationType, severity: Severity | undefined): NotificationCategory {
  switch (type) {
    case "alarm.opened":
    case "alarm.resolved":
      return severity === "critical" ? "alarms-critical" : "alarms-other";
    case "reporting.stopped":
    case "reporting.resumed":
      return "stopped-reporting";
  }
}

/**
 * The one defaults table (crmarr/templates transactional-email-content,
 * Mermera #1079: a box the page shows ticked must be a box the sender sends
 * on). The Account page and the fan-out both read it; a person with no
 * stored choice gets exactly this. Gairm staff start with everything off -
 * the alarms Slack channel is theirs - and may opt in.
 */
export const NOTIFICATION_CATEGORIES: ReadonlyArray<{
  id: NotificationCategory;
  label: string;
  covers: string;
  defaults: { customer: Record<NotificationChannel, boolean>; staff: Record<NotificationChannel, boolean> };
}> = [
  {
    id: "stopped-reporting",
    label: "An environment stopped reporting",
    covers: "Starsach has not heard from an environment's stack for three hours, and when it is back.",
    defaults: { customer: { email: true }, staff: { email: false } },
  },
  {
    id: "alarms-critical",
    label: "Critical alarms",
    covers: "A critical CloudWatch alarm went into alarm, and when it is back to OK.",
    defaults: { customer: { email: true }, staff: { email: false } },
  },
  {
    id: "alarms-other",
    label: "Other alarms",
    covers: "A warning or informational alarm went into alarm, and when it is back to OK.",
    defaults: { customer: { email: false }, staff: { email: false } },
  },
];

/** A person's stored choices: only what they changed. Anything absent is the default above. */
export const NotificationPreferencesSchema = z.partialRecord(NotificationCategorySchema, z.object({ email: z.boolean() }).partial());
export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

export function wantsNotification(prefs: NotificationPreferences | undefined, category: NotificationCategory, channel: NotificationChannel, staff: boolean): boolean {
  const chosen = prefs?.[category]?.[channel];
  if (chosen !== undefined) return chosen;
  const row = NOTIFICATION_CATEGORIES.find((c) => c.id === category)!;
  return row.defaults[staff ? "staff" : "customer"][channel];
}

/** `NotificationEvents`: one row per thing that happened. */
export const NotificationEventRecordSchema = z.object({
  organisation_id: z.string().min(1),
  /** `${occurred_at}#${event_id}` */
  sk: z.string().min(1),
  /** Deterministic from the source, so a redelivered source writes the row once (conditional put). */
  event_id: z.string().min(1),
  type: NotificationTypeSchema,
  environment_id: z.string().min(1),
  environment_name: z.string(),
  aws_account_number: z.string().optional(),
  severity: z.enum(["critical", "warning", "info"]).optional(),
  alarm_name: z.string().optional(),
  /** Connect's case number, display only (#525). */
  ticket: z.string().optional(),
  /** Stopped reporting: when the stack was last heard from. */
  last_heard_at: z.string().optional(),
  occurred_at: z.string(),
  ttl: z.number().int(),
});
export type NotificationEventRecord = z.infer<typeof NotificationEventRecordSchema>;

/**
 * `sent`, or why not: a refusal is never recorded as sent (Mermera #959).
 * `skipped` is decided at fan-out or before the send (no address, stale);
 * `not_sent` is the sender's own refusal (allow-list, suppression).
 */
export const OutboxStatusSchema = z.enum(["pending", "sending", "sent", "skipped", "not_sent", "failed"]);
export type OutboxStatus = z.infer<typeof OutboxStatusSchema>;

/** `NotificationOutbox`: one row per recipient and channel. */
export const OutboxRecordSchema = z.object({
  event_id: z.string().min(1),
  /** `${channel}#${user_id}` */
  sk: z.string().min(1),
  organisation_id: z.string().min(1),
  /** The `NotificationEvents` sort key, so the dispatcher can read the event. */
  event_sk: z.string().min(1),
  channel: NotificationChannelSchema,
  user_id: z.string().min(1),
  /** Where the recipient's own row lives: their organization, or `__internal__` for staff. */
  user_organisation_id: z.string().min(1),
  address: z.string().optional(),
  status: OutboxStatusSchema,
  reason: z.string().optional(),
  attempts: z.number().int(),
  created_at: z.string(),
  claimed_at: z.string().optional(),
  sent_at: z.string().optional(),
  ttl: z.number().int(),
});
export type OutboxRecord = z.infer<typeof OutboxRecordSchema>;
